import type { AlertInfoModel, AlertModel } from '@/shared/generated'

import { useMemo, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { cva } from 'class-variance-authority'
import {
	AlertTriangleIcon,
	EyeIcon,
	InfoIcon,
	PartyPopperIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'

import {
	usePricingAlertsAnnotationsQuery,
	usePricingAlertsQuery,
} from '@/entities/price-alert'

import { Operators } from '@/shared/components/query-builder'
import { Badge } from '@/shared/components/ui/badge'
import {
	Empty,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from '@/shared/components/ui/empty'
import {
	Item,
	ItemContent,
	ItemDescription,
	ItemGroup,
	ItemMedia,
	ItemTitle,
} from '@/shared/components/ui/item'
import { Spinner } from '@/shared/components/ui/spinner'
import {
	ToggleGroup,
	ToggleGroupItem,
} from '@/shared/components/ui/toggle-group'
import { AlertLevel } from '@/shared/generated'
import { cn } from '@/shared/lib/utils'
import { formatNumber, NumberFormats } from '@/shared/tools/locale'
import { getFiltersQueryObject } from '@/shared/tools/location'

import { Widget, WidgetContent, WidgetHeader } from '../../Widget'

export default function AlertsWidget({ className }: { className?: string }) {
	const { t } = useTranslation()
	const [filter, setFilter] = useState('all')
	const pricingAlertsAnnotations = usePricingAlertsAnnotationsQuery()
	const pricingAlerts = usePricingAlertsQuery({})
	const counts = useMemo(
		() =>
			pricingAlerts.data?.by_id.reduce(
				(acc, alert) => {
					acc[alert.level as AlertLevel] =
						(acc[alert.level as AlertLevel] || 0) + 1

					return acc
				},
				{} as Record<AlertLevel, number>
			),
		[pricingAlerts.data]
	)

	const alertsList = useMemo(() => {
		const metaMap = new Map<string, AlertModel>(
			pricingAlertsAnnotations.data?.map((annotation) => [
				annotation.id,
				annotation,
			])
		)

		return pricingAlerts.data?.by_id
			.sort((a, b) => b.est_revenue - a.est_revenue)
			.sort(
				sortedLevels([
					AlertLevel.Critical,
					AlertLevel.Warning,
					AlertLevel.Unassigned,
				])
			)
			.map((alert) => ({
				alert,
				meta: metaMap.get(alert.id!)!,
			}))
			.filter((alert) => !!alert.meta)
			.filter((alert) => {
				if (filter === 'all') return true
				if (filter === 'critical')
					return alert.meta?.level === AlertLevel.Critical
				if (filter === 'warning')
					return alert.meta?.level === AlertLevel.Warning
				if (filter === 'info')
					return alert.meta?.level === AlertLevel.Unassigned

				return false
			})
	}, [pricingAlerts.data, pricingAlertsAnnotations.data, filter])

	const isLoading =
		pricingAlerts.isLoading || pricingAlertsAnnotations.isLoading

	return (
		<Widget className={cn('relative', className)}>
			<WidgetHeader
				title={t('widget.alerts.title', {
					defaultValue: 'Alerts',
				})}
			/>

			<WidgetContent className='flex flex-col gap-2 overflow-auto'>
				<ToggleGroup
					type='single'
					size='sm'
					defaultValue='all'
					variant='default'
					spacing={2}
					value={filter}
					onValueChange={(value) => setFilter(value)}
				>
					<ToggleGroupItem value='all' aria-label='Toggle all'>
						{t('all')}
					</ToggleGroupItem>
					<ToggleGroupItem value='critical' aria-label='Toggle critical'>
						{t('critical')}
						<span className='text-xs text-muted-foreground'>
							{counts?.[AlertLevel.Critical]}
						</span>
					</ToggleGroupItem>
					<ToggleGroupItem value='warning' aria-label='Toggle warning'>
						{t('warning')}
						<span className='text-xs text-muted-foreground'>
							{counts?.[AlertLevel.Warning]}
						</span>
					</ToggleGroupItem>
					<ToggleGroupItem value='info' aria-label='Toggle unassigned'>
						{t('unassigned')}
						<span className='text-xs text-muted-foreground'>
							{counts?.[AlertLevel.Unassigned]}
						</span>
					</ToggleGroupItem>
				</ToggleGroup>
				<ItemGroup className='h-100 scroll-fade overflow-y-auto' key={filter}>
					{!isLoading && !alertsList?.length && (
						<Empty>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<PartyPopperIcon />
								</EmptyMedia>
								<EmptyTitle>
									{t('no_alerts', { defaultValue: 'No alerts to show' })}
								</EmptyTitle>
								<EmptyDescription>
									{t('widget.alerts.empty.description', {
										defaultValue:
											'Looks like you have no alerts to show. You are on a good track!',
									})}
								</EmptyDescription>
							</EmptyHeader>
						</Empty>
					)}
					{isLoading && (
						<Empty>
							<EmptyHeader>
								<EmptyMedia variant='icon'>
									<Spinner />
								</EmptyMedia>
								<EmptyDescription>{t('loading')}</EmptyDescription>
							</EmptyHeader>
						</Empty>
					)}

					{alertsList?.map((alert) => {
						const formattedMetrics = {
							products: formatNumber(alert.alert.products),
							revenue: formatNumber(
								alert.alert.est_revenue_share,
								NumberFormats.Percent
							),
						}

						return (
							<Item
								variant='outline'
								size='xs'
								className={alertCard({ severity: alert.meta?.level })}
								key={alert.alert.id}
								asChild
							>
								<Link
									to='/p/products'
									search={getFiltersQueryObject([
										{
											name: 'alerts',
											operation: Operators.ANY_OF,
											value: [alert.alert.id!],
										},
									])}
								>
									<ItemMedia variant='icon'>
										{alert.meta?.level === AlertLevel.Critical ? (
											<AlertTriangleIcon className='h-4 w-4 text-red-500' />
										) : alert.meta?.level === AlertLevel.Warning ? (
											<EyeIcon className='h-4 w-4 text-yellow-500' />
										) : (
											<InfoIcon className='h-4 w-4 text-gray-500' />
										)}
									</ItemMedia>
									<ItemContent>
										<div className='mb-1 flex items-center justify-between'>
											<ItemTitle className='text-[13px]'>
												{t(alert.meta.name, { name: alert.meta?.name })}
											</ItemTitle>
											<Badge
												className={alertBadge({ severity: alert.meta?.level })}
											>
												{formattedMetrics.products} ({formattedMetrics.revenue})
											</Badge>
										</div>
										<ItemDescription>
											{t(alert.meta.description, {
												name: alert.meta?.name,
											})}
										</ItemDescription>
									</ItemContent>
								</Link>
							</Item>
						)
					})}
				</ItemGroup>
			</WidgetContent>
		</Widget>
	)
}
const sortedLevels =
	(levels: AlertLevel[]) =>
	(a: AlertInfoModel | AlertModel, b: AlertInfoModel | AlertModel) => {
		const aIndex = levels.indexOf(a.level! as AlertLevel)
		const bIndex = levels.indexOf(b.level! as AlertLevel)

		if (aIndex > bIndex) return 1
		if (aIndex < bIndex) return -1

		return 0
	}
const alertCard = cva('', {
	variants: {
		severity: {
			[AlertLevel.Warning]:
				'[a]:hover:bg-yellow-100 dark:[a]:hover:bg-yellow-700/50 bg-yellow-100/50 text-yellow-700 dark:bg-yellow-900/50 dark:text-yellow-200 border border-yellow-200 dark:border-yellow-800',
			[AlertLevel.Unassigned]:
				'[a]:hover:bg-neutral-100 dark:[a]:hover:bg-neutral-700/50 bg-neutral-100/50 text-neutral-700 dark:bg-neutral-900/50 dark:text-neutral-200 border border-neutral-200 dark:border-neutral-800',
			[AlertLevel.Critical]:
				'[a]:hover:bg-red-100 dark:[a]:hover:bg-red-700/50 bg-red-100/30 border-red-200/50 text-red-700 dark:bg-red-900/30 dark:border-red-800/50 dark:text-red-200',
		},
	},
})

const alertBadge = cva('', {
	variants: {
		severity: {
			[AlertLevel.Warning]:
				'bg-yellow-200 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-200',
			[AlertLevel.Unassigned]:
				'bg-neutral-200 text-neutral-700 dark:bg-neutral-900 dark:text-neutral-200',
			[AlertLevel.Critical]:
				'bg-red-200 border-red-200/50 text-red-700 dark:bg-red-900/50 dark:border-red-800/50 dark:text-red-200',
		},
	},
})
